import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FaChevronDown, FaChevronRight, FaSyncAlt } from "react-icons/fa";
import apiAxios from "helpers/apiAxios";

/**
 * Every call the Portal made to the ERP for this request (erp_ax_gateway_logs):
 * what was sent, what AX answered, how long it took, and the Idempotency-Key to
 * quote when asking the ERP team "did this land?". Loaded only when opened, and
 * again whenever `refreshKey` changes (after Save / Create / Update).
 *
 * Read errors are shown inline rather than toasted: this panel is a diagnostic
 * aid and must not add noise to the journal actions above it.
 */
const OPERATION_LABELS = {
  create_payment: "Create journal",
  amend_payment: "Update journal",
  get_ledger_journal: "Read journal",
  delete_ledger_journal: "Delete journal",
  list_ledger_journals: "List journals",
  get_vendors: "Vendor lookup",
  get_master_data: "AX list lookup",
  get_purchase_order: "Purchase order",
  auth_token: "Sign in",
  other: "Other",
};

const formatTime = (iso) => (iso ? String(iso).replace("T", " ").slice(0, 19) : "—");

const pretty = (text) => {
  if (!text) return "—";
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
};

const LogDetail = ({ log }) => (
  <div className="payment-journal-log-detail">
    <dl>
      <div><dt>Request</dt><dd className="tw-font-mono">{log.http_method} {log.path}</dd></div>
      <div><dt>Idempotency-Key</dt><dd className="tw-font-mono">{log.idempotency_key || "—"}</dd></div>
      <div><dt>Correlation ID</dt><dd className="tw-font-mono">{log.correlation_id || "—"}</dd></div>
    </dl>
    <div className="payment-journal-log-bodies">
      <div><p>Sent to ERP</p><pre>{pretty(log.request_payload)}</pre></div>
      <div><p>ERP answer</p><pre>{pretty(log.response_body)}</pre></div>
    </div>
  </div>
);

LogDetail.propTypes = { log: PropTypes.object.isRequired };

const ErpLogPanel = ({ requestId, journalNames = [], refreshKey = 0 }) => {
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [journal, setJournal] = useState("");
  const [status, setStatus] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiAxios.get(`/erp/payment_requests/${requestId}/erp_logs`, {
        params: { journal: journal || undefined, status: status || undefined },
        // A panel of the journal tab: a 403 must not replace the whole request page.
        skipAccessDeniedPage: true,
      });
      setLogs(data?.data || []);
      setError("");
    } catch (requestError) {
      setLogs([]);
      setError(requestError?.response?.status === 403 ? "You cannot see the ERP log of this request." : "Could not load the ERP log.");
    } finally {
      setLoading(false);
    }
  }, [requestId, journal, status]);

  useEffect(() => { if (open) load(); }, [open, load, refreshKey]);

  const failed = logs.filter((l) => l.status === "failed").length;

  return (
    <section className="payment-journal-log" aria-label="ERP log">
      <div className="payment-journal-log-head">
        <button type="button" className="payment-journal-log-toggle" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? <FaChevronDown aria-hidden="true" size={10} /> : <FaChevronRight aria-hidden="true" size={10} />}
          ERP log
          {open && !loading && <span className="payment-journal-log-count">{logs.length}{failed ? ` · ${failed} failed` : ""}</span>}
        </button>
        {open && (
          <div className="payment-journal-log-filters">
            {journalNames.length > 1 && (
              <select aria-label="Journal" value={journal} onChange={(e) => setJournal(e.target.value)}>
                <option value="">All journals</option>
                {journalNames.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            )}
            <select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All calls</option>
              <option value="failed">Failed only</option>
              <option value="success">Successful only</option>
            </select>
            <button type="button" onClick={load} disabled={loading} className="payment-journal-log-refresh" aria-label="Reload ERP log">
              <FaSyncAlt aria-hidden="true" size={11} className={loading ? "tw-animate-spin" : undefined} />
            </button>
          </div>
        )}
      </div>

      {open && (
        <div className="payment-journal-log-body">
          {error && <p role="alert" className="payment-journal-log-empty payment-journal-cell-warn">{error}</p>}
          {!error && !loading && logs.length === 0 && <p className="payment-journal-log-empty">No ERP call recorded for this request yet.</p>}
          {logs.length > 0 && (
            <table className="payment-journal-log-table">
              <thead>
                <tr><th>Time</th><th>Journal</th><th>Operation</th><th>Result</th><th>HTTP</th><th>ms</th><th>By</th><th>Error</th></tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <tr className={log.status === "failed" ? "payment-journal-log-failed" : undefined}
                      onClick={() => setExpanded((id) => (id === log.id ? null : log.id))}
                      aria-expanded={expanded === log.id} tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter") setExpanded((id) => (id === log.id ? null : log.id)); }}>
                      <td className="tw-whitespace-nowrap tw-tabular-nums">{formatTime(log.fired_at)}</td>
                      <td>{log.journal_name || "—"}</td>
                      <td>{OPERATION_LABELS[log.operation] || log.operation}</td>
                      <td><span className={`payment-journal-log-status payment-journal-log-status-${log.status}`}>{log.status === "failed" ? "Failed" : "OK"}</span></td>
                      <td className="tw-tabular-nums">{log.http_code || "—"}</td>
                      <td className="tw-tabular-nums">{log.duration_ms ?? "—"}</td>
                      <td>{log.user_name || "—"}</td>
                      <td className="payment-journal-log-error" title={log.error || undefined}>{log.error || ""}</td>
                    </tr>
                    {expanded === log.id && (
                      <tr className="payment-journal-log-detail-row"><td colSpan={8}><LogDetail log={log} /></td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
};

ErpLogPanel.propTypes = {
  requestId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  journalNames: PropTypes.arrayOf(PropTypes.string),
  refreshKey: PropTypes.number,
};

export default ErpLogPanel;
